import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router'
import * as Sentry from '@sentry/browser'
import queryString from 'query-string'

import { name, version } from '../package.json'
import { App } from './App'
import { unregisterAllServiceWorkers } from './helpers/serviceWorker'
import { getLocale, setLanguage } from './helpers/trans'
import { AppInterceptor } from './interceptors/AppInterceptor'
import { AuthInterceptor } from './interceptors/AuthInterceptor'
import { setApiToken, setRefreshToken, setTenant } from './redux/authentication/authenticationActions'
import { configureStore, TypedDispatch } from './redux/store'

import './interceptors/JsonApiInterceptor'
import './interceptors/PageSizeInterceptor'
import './styles/legacy/main.scss'
import './styles/global.scss'

if (process.env.NODE_ENV === 'production') {
    Sentry.init({
        dsn: 'https://80f4b6f8ea2e4eb3bd5ab3a2fd6b22d2@o62714.ingest.sentry.io/5226639',
        release: `${name}@${version}`,
        environment: process.env.REACT_APP_APP_ENV,
    })
}

const locale = getLocale()
// Remembers a language from the url (e.g. `?language=de` in the ValkWorks app) for the next visits
setLanguage(locale)

unregisterAllServiceWorkers()

// The browser's own scroll restoration on reload fights views that manage their scroll position themselves (e.g. the
// Valk Talk chat, which always lands at the bottom of a conversation), so the app opts out of it globally
if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual'
}

// query-string returns an array for a repeated parameter, only a single value is used
const getSingleValue = (value: string | (string | null)[] | null | undefined): string | undefined => (
    typeof value === 'string' ? value : undefined
)

const getTokensFromUrl = (dispatch: TypedDispatch) => {
    const url = window.location.search
    const params = queryString.parse(url)

    const tenant = getSingleValue(params.tenant)
    const apiToken = getSingleValue(params.api_token)

    if (tenant) {
        dispatch(setTenant(tenant))
    }

    if (apiToken) {
        dispatch(setApiToken(apiToken))
        dispatch(setRefreshToken(getSingleValue(params.refresh_token)))
    }
}

export const appRootId = 'app-root'
const container = document.getElementById(appRootId)

if (! container) {
    throw new Error(`[index] Element #${appRootId} not found in index.html`)
}

const root = createRoot(container)

configureStore().then(store => {
    getTokensFromUrl(store.dispatch)
    const authInterceptor = new AuthInterceptor(store)
    const appInterceptor = new AppInterceptor(store)
    authInterceptor.setInterceptors()
    appInterceptor.setInterceptors()

    root.render(
        <Provider store={store}>
            <BrowserRouter>
                <App />
            </BrowserRouter>
        </Provider>,
    )
})
